import { LoadingButton } from '@mui/lab';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { noop } from 'lodash';
import PropTypes from 'prop-types';
import { useCallback, useRef, useState } from 'react';

import CheckboxWithInfoTooltip from 'common/components/CheckboxWithInfoTooltip';

const InfoDialogCheckbox = ({ checkbox, isWarning, initialChecked, onChange }) => {
  const [isChecked, setIsChecked] = useState(initialChecked);

  const handleChange = (checked) => {
    onChange(checked);
    setIsChecked(checked);
  };

  return (
    <CheckboxWithInfoTooltip
      id='useInfoDialog-checkbox'
      label={checkbox.label}
      helperText={checkbox.helperText}
      checked={isChecked}
      onChange={handleChange}
      sx={{ mr: 'auto', ml: 1, order: isWarning ? 1 : -1 }}
    />
  );
};

InfoDialogCheckbox.propTypes = {
  checkbox: PropTypes.shape({
    label: PropTypes.string.isRequired,
    helperText: PropTypes.string.isRequired,
  }).isRequired,
  isWarning: PropTypes.bool,
  initialChecked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
};

InfoDialogCheckbox.defaultProps = {
  isWarning: false,
};

const useInfoDialog = () => {
  const [content, setContent] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const checkedRef = useRef(false);

  const confirmDialog = useCallback(async () => {
    setSubmitting(true);
    await content.callback(checkedRef.current);
    setContent(null);
    setSubmitting(false);
  }, [content]);

  const closeDialog = () => setContent(null);

  const showInfoDialog = ({
    title,
    message,
    callback = noop,
    cancelable = false,
    confirmButtonText = 'OK',
    isWarning,
    closeInfo,
    checkbox,
  }) => {
    checkedRef.current = false;
    setContent({
      title,
      message,
      callback,
      cancelable,
      confirmButtonText,
      isWarning,
      closeInfo,
      checkbox,
    });
  };

  if (content?.closeInfo) {
    closeDialog();
  }

  const InfoDialog = () => {
    if (!content) {
      return null;
    }

    return (
      <Dialog open={!!content} aria-labelledby='useInfoDialog-dialog-title' transitionDuration={0}>
        <DialogTitle id='useInfoDialog-dialog-title' sx={{ fontWeight: 600 }}>
          {content.title}
        </DialogTitle>

        <DialogContent>{content.message}</DialogContent>

        <DialogActions
          sx={{
            flexDirection: content.isWarning ? 'row-reverse' : 'row',
            justifyContent: content.isWarning ? 'flex-start' : 'flex-end',
          }}
        >
          {content.checkbox && (
            <InfoDialogCheckbox
              checkbox={content.checkbox}
              isWarning={content.isWarning}
              initialChecked={checkedRef.current}
              onChange={(checked) => {
                checkedRef.current = checked;
              }}
            />
          )}
          {content.cancelable && (
            <Button
              id='useInfoDialog-cancel-button'
              onClick={closeDialog}
              color='secondary'
              size='medium'
              variant='outlined'
              sx={{ ml: 1 }}
              disabled={submitting}
            >
              Cancel
            </Button>
          )}

          <LoadingButton
            loading={submitting}
            id='useInfoDialog-confirm-button'
            onClick={confirmDialog}
            color={content.isWarning ? 'error' : 'primary'}
            size='medium'
            variant='contained'
          >
            {content.confirmButtonText}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    );
  };

  return { InfoDialog, showInfoDialog };
};

export default useInfoDialog;
